import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Card, Typography, Select, Space, Tag, Spin, Pagination, Tooltip, Input } from 'antd';
import { 
    KeyOutlined, 
    UserOutlined, 
    BookOutlined, 
    DollarCircleOutlined, 
    TagOutlined, 
    CalendarOutlined, 
    FilterOutlined, 
    SendOutlined,
    SearchOutlined 
} from '@ant-design/icons';
import { toast } from 'react-toastify';
// Bu import yolunun projenizin yapısına göre doğru olduğundan emin olun
import { axiosInstance } from '../../api/axiosInstance'; 
import styled from 'styled-components';

const { Option } = Select;
const { Search } = Input;

// --- API Uç Noktaları ---
const GET_ALL_PAYMENTS_URL = '/Payments/GetAllByDynamic'; 
const UPDATE_STATUS_URL = '/Payments/Update'; 

// --- Styled Components ---
const StyledPageContainer = styled.div`
    padding: 6px; 
    min-height: calc(100vh - 64px); 
`;

const StyledContentCard = styled(Card)`
    border-radius: 10px; 
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1); 
    border: none; 
    
    .ant-card-body {
        padding: 10px 16px; 
    }
`;

const HeaderContainer = styled.div`
    display: flex;
    justify-content: space-between; 
    align-items: center;
    margin-bottom: 12px; 
    flex-wrap: wrap;
    gap: 16px;
`;

const StyledTable = styled(Table)`
    .ant-table-thead > tr > th {
        background-color: #e9ecef; 
        color: #343a40; 
        font-weight: 600;
        border-bottom: 2px solid #dee2e6;
    }
    
    .ant-table-tbody > tr > td {
        padding-top: 12px; 
        padding-bottom: 12px; 
    }

    .ant-table-tbody > tr.ant-table-row:hover > td {
        background: #f8f9fa; 
        cursor: pointer;
    }
`;


const FilterContainer = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
`;

// --- Sabitler ---
const PAGE_SIZE_OPTIONS = [10, 25, 50]; 

const STATUS_OPTIONS = [
    "Geri Ödeme Yapıldı.",
    "Geri Ödeme Bekleniyor.",
    "Ödeme yapılmadığı için iptal edildi.",
    "Kurs aktarma için iptal edildi.",
];

const STATUS_COLOR_MAP = {
    "Geri Ödeme Yapıldı.": 'success',
    "Geri Ödeme Bekleniyor.": 'processing',
    "Ödeme yapılmadığı için iptal edildi.": 'error',
    "Kurs aktarma için iptal edildi.": 'warning',
    "Diğer": 'default',
};


const Refunds = () => {
    const [allPayments, setAllPayments] = useState([]); 
    
    // UI ve Sayfalama State'leri
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [selectedStatus, setSelectedStatus] = useState(null); 
    const [searchTerm, setSearchTerm] = useState(''); 
    const [updatingRecordId, setUpdatingRecordId] = useState(null); 

    const fetchAllPayments = useCallback(async () => {
        setLoading(true);
        
        const dynamicQuery = {
            dynamic: {
                sort: [
                    { field: "createdAt", dir: "desc" }
                ],
                filter: { 
                    field: "paymentType", 
                    operator: "eq",
                    value: "BankTransfer"
                }
            },
            pageRequest: {
                page: 0, 
                pageSize: 10000 // Yüksek limit ile tüm veriyi çek
            }
        };
        
        try {
            const response = await axiosInstance.post(GET_ALL_PAYMENTS_URL, dynamicQuery); 
            
            setAllPayments(response.data.items || []); 
            
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Geri ödeme kayıtları yüklenirken bir hata oluştu.";
            toast.error(errorMessage);
            setAllPayments([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAllPayments();
    }, [fetchAllPayments]);
    
    const filteredAndSearchedData = useMemo(() => {
        let currentData = allPayments;

        if (selectedStatus) {
            currentData = currentData.filter(item => item.status === selectedStatus);
        }

        if (searchTerm) {
            const lowerCaseSearchTerm = searchTerm.toLocaleLowerCase('tr-TR');
            
            currentData = currentData.filter(item => {
                
                // ID
                const idMatch = item.id?.toString().includes(lowerCaseSearchTerm);
                
                // Kullanıcı Adı/Soyadı 
                const userFullName = `${item.user?.firstName} ${item.user?.lastName}`;
                const userMatch = userFullName.toLocaleLowerCase('tr-TR').includes(lowerCaseSearchTerm);
                
                const tcMatch = item.user?.tc?.toLocaleLowerCase('tr-TR').includes(lowerCaseSearchTerm);
                
                const courseMatch = item.course?.name?.toLocaleLowerCase('tr-TR').includes(lowerCaseSearchTerm);
                
                const semesterMatch = item.course?.semester?.toLocaleLowerCase('tr-TR').includes(lowerCaseSearchTerm);
                
                const statusMatch = item.status?.toLocaleLowerCase('tr-TR').includes(lowerCaseSearchTerm);
                
                return idMatch || userMatch || tcMatch || courseMatch || semesterMatch || statusMatch;
            });
        }

        if (currentPage !== 1 && currentData.length <= (currentPage - 1) * pageSize) {
             setCurrentPage(1);
        }
        
        return currentData;
    }, [allPayments, selectedStatus, searchTerm, currentPage, pageSize]);
    
    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return filteredAndSearchedData.slice(startIndex, endIndex);
    }, [filteredAndSearchedData, currentPage, pageSize]);


    const handleTableChange = (page, size) => {
        setCurrentPage(page);
        if (size !== pageSize) {
            setPageSize(size);
        }
    };
    
    const handleStatusUpdate = async (recordId, newStatus) => {
        setUpdatingRecordId(recordId); 
        
        try {
            const updatePayload = {
                id: recordId, 
                status: newStatus 
            };

            await axiosInstance.put(UPDATE_STATUS_URL, updatePayload);
            
            toast.success(`Kayıt #${recordId} durumu başarıyla "${newStatus}" olarak güncellendi.`);
            
            // Başarılı güncelleme sonrası sadece allPayments state'indeki ilgili kaydı güncelle
            setAllPayments(prevPayments => prevPayments.map(payment => 
                payment.id === recordId ? { ...payment, status: newStatus } : payment
            ));
            
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Durum güncelleme sırasında bir hata oluştu.";
            toast.error(`Durum güncellenemedi: ${errorMessage}`);
            
        } finally {
            setUpdatingRecordId(null);
        }
    };


    // --- Tablo Sütunları ---
    const columns = [
        {
            title: (<Space size={4}><KeyOutlined /> Kayıt ID</Space>),
            dataIndex: 'id',
            key: 'id',
            width: 80,
        },
        {
            title: (<Space size={4}><UserOutlined /> Kullanıcı Adı/Soyadı</Space>),
            key: 'userName',
            render: (record) => (
                <Space direction="vertical" size={6}> 
                    <Typography.Text strong>{record.user?.firstName} {record.user?.lastName}</Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                        TC: {record.user?.tc || 'Belirtilmemiş'}
                    </Typography.Text>
                </Space>
            ),
        },
        {
            title: (<Space size={4}><BookOutlined /> Kurs Bilgileri</Space>),
            key: 'courseInfo',
            render: (record) => (
                <Space direction="vertical" size={6}>
                    <Typography.Text strong>
                        <Tag color="default" style={{ marginRight: 6 }}>#{record.course?.id}</Tag>
                        {record.course?.name}
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                        Dönem: {record.course?.semester || '-'}
                    </Typography.Text>
                </Space>
            ),
        },
        {
            title: (<Space size={4}><DollarCircleOutlined /> Fiyat</Space>),
            dataIndex: 'price',
            key: 'price',
            render: (price) => (
                <Tag color="green">
                    {price ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(price) : '0,00 ₺'}
                </Tag>
            ),
        },
        {
            title: (<Space size={4}><TagOutlined /> Durum</Space>),
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                const color = STATUS_COLOR_MAP[status] || STATUS_COLOR_MAP["Diğer"];
                return (
                    <Tag color={color} key={status}>
                        {status || 'Bilinmiyor'}
                    </Tag>
                );
            },
        },
        {
            title: (<Space size={4}><CalendarOutlined /> Tarih</Space>),
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date) => {
                if (!date) return '-';
                
                const formattedDate = new Date(date).toLocaleString('tr-TR', { 
                    year: 'numeric', 
                    month: 'numeric', 
                    day: 'numeric', 
                    hour: '2-digit', 
                    minute: '2-digit' 
                });
                return formattedDate;
            },
        },
        {
            title: 'İşlem',
            key: 'action',
            width: 150,
            render: (record) => (
                <Tooltip title="Durumu Güncelle">
                    <Select
                        defaultValue={record.status}
                        disabled={loading || updatingRecordId === record.id} 
                        style={{ width: '100%' }}
                        onChange={(newStatus) => handleStatusUpdate(record.id, newStatus)}
                        dropdownMatchSelectWidth={300} 
                        suffixIcon={
                            updatingRecordId === record.id ? <Spin size="small" /> : <SendOutlined />
                        }
                    >
                        {STATUS_OPTIONS.map(status => (
                            <Option key={status} value={status}>
                                {status}
                            </Option>
                        ))}
                    </Select>
                </Tooltip>
            ),
        },
    ];

    return (
        <StyledPageContainer>
            <StyledContentCard>
                <HeaderContainer>
                    <Search
                        placeholder="Tüm tablolarda ara (ID, Ad, Soyad, Kurs, TC, Durum...)"
                        allowClear
                        enterButton={<SearchOutlined />}
                        style={{ width: 400 }}
                        onSearch={setSearchTerm}
                        onChange={(e) => { 
                            if (!e.target.value) setSearchTerm(''); 
                        }}
                    />

                    <FilterContainer>
                        <FilterOutlined />
                        <Space size="middle">
                            <Select
                                allowClear
                                placeholder="Duruma Göre Filtrele"
                                style={{ width: 250 }}
                                onChange={setSelectedStatus}
                                value={selectedStatus}
                            >
                                {STATUS_OPTIONS.map(status => (
                                    <Option key={status} value={status}>
                                        {status}
                                    </Option>
                                ))}
                            </Select>
                        </Space>
                    </FilterContainer>
                </HeaderContainer>

                <Spin spinning={loading}>
                    <StyledTable
                        columns={columns}
                        dataSource={paginatedData} 
                        loading={loading}
                        rowKey="id"
                        size="middle" 
                        scroll={{ x: 'max-content' }} 
                        pagination={false} 
                        locale={{
                            emptyText: 'Filtreye uygun geri ödeme kaydı bulunmamaktadır.'
                        }}
                    />
                </Spin>
                
                {!loading && filteredAndSearchedData.length > 0 && (
                    <Pagination
                        style={{ marginTop: 10, textAlign: 'right' }} 
                        current={currentPage}
                        pageSize={pageSize}
                        total={filteredAndSearchedData.length} 
                        onChange={handleTableChange}
                        onShowSizeChange={handleTableChange}
                        pageSizeOptions={PAGE_SIZE_OPTIONS}
                        showSizeChanger
                        showTotal={(total, range) => `${range[0]}-${range[1]} / Toplam ${total} kayıt`}
                    />
                )}
            </StyledContentCard>
        </StyledPageContainer>
    );
};

export default Refunds;